import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useAuth } from "@hoteltradergit/auth";
import { getProperties, checkIsPilotProperty } from "../api";
import resolveLinkedProperty from "../utils/resolveLinkedProperty";
import Loader from "../components/Loader";
import { OFFLINE_CRS_ID } from "../constants";
import {
  selectSelectedRateplan,
  isNegotiatedRateplan,
  setSelectedRateplan,
  setEditableFilter,
  EDITABLE_FILTER,
} from "../slices/rateplanSlice";
import { setSelectedRoomtype } from "../slices/roomtypeSlice";
import { resetDateRanges } from "../slices/ariDateRangeSlice";
import useLocalStorage from "../hooks/useLocalStorage";
import { trackHeapEvent } from "@hoteltradergit/ht-telemetry-ui-lib";
import { ANALYTICS_EVENTS } from "../constants/analyticsEvents";
import { P, usePermission } from "../rbac";

export const PropertyStoreContext = React.createContext({});

export const PropertyStoreProvider = React.memo(function PropertyStoreProvider({
  children,
}) {
  const { idToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedProperty, setSelectedProperty] = useState(null);
  // True once the initial fetch has settled, whether or not it produced a selection. Consumers
  // that scope data by hotel (Manage Users) need to tell "still resolving" — where `null` is
  // normal — from "resolved to nothing", which is a fault worth surfacing.
  const [selectionResolved, setSelectionResolved] = useState(false);
  const [isPilotHotel, setIsPilotHotel] = useState();
  const [propertyCode, setPropertyCode] = useLocalStorage("pcode", null);
  // Cache the full selected property so it can be restored on reload even when
  // it isn't on the first page (search is server-side and name-based, so we
  // can't look a property up by its code).
  const [storedProperty, setStoredProperty] = useLocalStorage(
    "pselected",
    null
  );
  const dispatch = useDispatch();
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (!idToken) {
      return;
    }

    // Skip if already fetching (prevents double-fetch in StrictMode)
    if (isFetchingRef.current) {
      setLoading(false);
      return;
    }

    // Skip if we already have properties loaded
    if (properties.length > 0) {
      setLoading(false);
      return;
    }

    hasFetchedRef.current = true;
    isFetchingRef.current = true;
    let isMounted = true;
    setLoading(true);

    // Persist the resolved selection to state and localStorage.
    const applySelection = (selectedProp) => {
      setSelectedProperty(selectedProp);
      if (
        selectedProp?.propertyCode &&
        selectedProp.propertyCode !== propertyCode
      ) {
        setPropertyCode(selectedProp.propertyCode);
      }
    };

    // An email deep link's property is resolved alongside the first page, so the
    // loader covers both and the app opens on the linked hotel (EO-1893).
    Promise.all([
      getProperties({ page: 1, limit: 20 }),
      resolveLinkedProperty(document.location.search),
    ])
      .then(([res, linkedProperty]) => {
        isFetchingRef.current = false;
        setLoading(false);
        setSelectionResolved(true);
        const data = res?.data?.data;
        const sortedProperties =
          data?.properties?.sort((a, b) =>
            a.propertyName?.localeCompare(b.propertyName)
          ) || [];

        // React 18 automatically batches these state updates
        setProperties(sortedProperties);
        setTotalCount(data?.totalCount ?? sortedProperties.length);

        // Priority 0: the property a recommendation deep link belongs to. Cached
        // like a manual switch, since it may sit beyond the first page.
        if (linkedProperty) {
          applySelection(linkedProperty);
          setStoredProperty(linkedProperty);
          return;
        }

        // Resolve the initially selected property.
        // Priority 1: URL parameter, Priority 2: localStorage.
        let params = new URLSearchParams(document.location.search);
        const code = params.get("pcode") || propertyCode;

        if (!code) {
          applySelection(sortedProperties[0] ?? null);
          return;
        }

        // Prefer an exact match from the first page.
        const localMatch = sortedProperties.find(
          (property) => property.propertyCode === code
        );
        if (localMatch) {
          applySelection(localMatch);
          return;
        }

        // Not on the first page — restore the cached property object if it
        // matches the persisted code (we can't fetch it by code).
        if (storedProperty?.propertyCode === code) {
          applySelection(storedProperty);
          return;
        }

        // Can't resolve the code (e.g. a shared URL for a property beyond the
        // first page with no cache) — clear stale data and default to the first.
        if (code === propertyCode) {
          setPropertyCode(null);
          setStoredProperty(null);
        }
        applySelection(sortedProperties[0] ?? null);
      })
      .catch((error) => {
        // Prevent state updates if component has unmounted
        if (!isMounted) return;

        isFetchingRef.current = false;
        setLoading(false);
        setSelectionResolved(true);
        toast.error("Unable to load property, please try again later");
      });

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken]);
  // Note: propertyCode and properties are intentionally not in dependencies to prevent refetch loop.
  // - propertyCode: only read for initial property selection, changes should not trigger refetch
  // - properties: checked to prevent duplicate fetches in React 18 Strict Mode, but changes should not trigger refetch

  useEffect(() => {
    if (!selectedProperty?.propertyId) {
      setIsPilotHotel(false);
      return;
    }

    checkIsPilotProperty(selectedProperty.propertyId)
      .then((response) => {
        if (response?.data?.isPilot === true) {
          setIsPilotHotel(true);
        } else {
          setIsPilotHotel(false);
        }
      })
      .catch(() => {
        setIsPilotHotel(false);
      });
  }, [selectedProperty?.propertyId]);

  const handlePropertyChange = useCallback(
    (property) => {
      dispatch(setSelectedRoomtype(null));
      dispatch(setSelectedRateplan(null));
      dispatch(setEditableFilter(EDITABLE_FILTER.ALL));
      dispatch(resetDateRanges());
      setSelectedProperty(property);
      trackHeapEvent(ANALYTICS_EVENTS.PROPERTY_SWITCH, {
        propertyName: property?.propertyName,
      });
      // Save property code + full object to localStorage so the selection
      // persists across reloads even when it isn't on the first page.
      if (property?.propertyCode) {
        setPropertyCode(property.propertyCode);
      }
      setStoredProperty(property ?? null);
    },
    [dispatch, setPropertyCode, setStoredProperty]
  );

  const contextValue = useMemo(
    () => ({
      selectedProperty,
      selectionResolved,
      setSelectedProperty: handlePropertyChange,
      properties,
      totalCount,
      isPilotHotel,
    }),
    [
      properties,
      totalCount,
      selectedProperty,
      selectionResolved,
      handlePropertyChange,
      isPilotHotel,
    ]
  );

  return (
    <PropertyStoreContext.Provider value={contextValue}>
      {<Loader loading={loading} />}
      {children}
    </PropertyStoreContext.Provider>
  );
});

export const usePropertyStore = () => useContext(PropertyStoreContext);

export const useIsOfflineProperty = () => {
  const { selectedProperty } = usePropertyStore();
  return selectedProperty?.crsId === OFFLINE_CRS_ID;
};

export const useIsEditableRateplan = (rateplan) => {
  const isOffline = useIsOfflineProperty();
  const selectedRateplan = useSelector(selectSelectedRateplan);
  const { allowed: canManageAri } = usePermission(P.Ari.Manage);
  return (
    canManageAri &&
    (isOffline || isNegotiatedRateplan(rateplan ?? selectedRateplan))
  );
};

export const useIsPilotHotel = () => {
  const { isPilotHotel } = usePropertyStore();
  return isPilotHotel;
};
